import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  CssBaseline,
  Stack,
  Grid,
} from '@mui/material';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import '../App.css';

// Custom dark theme (aligned with EVTool.js)
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontSize: '0.875rem',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': { color: '#ffffff' }, // Double ensure white text
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: { backgroundColor: '#333333', color: '#ffffff' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontSize: '0.875rem',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderRadius: 0,
          textTransform: 'none',
          padding: '8px 16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          borderRadius: 0,
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
        },
      },
    },
  },
});

const Calculators = () => {
  // State for Odds Converter
  const [fraction, setFraction] = useState('');
  const [decimal, setDecimal] = useState('');
  const [american, setAmerican] = useState('');
  const [implied, setImplied] = useState('');

  // State for Parlay Calculator
  const [parlayLegs, setParlayLegs] = useState(['', '']);
  const [parlayLegErrors, setParlayLegErrors] = useState(['', '']);
  const [parlayOdds, setParlayOdds] = useState(null);
  const [oddsFormat, setOddsFormat] = useState('american'); // Default to American

  // State for Poisson Calculator
  const [poissonMean, setPoissonMean] = useState('');
  const [poissonLine, setPoissonLine] = useState('');
  const [poissonMeanError, setPoissonMeanError] = useState('');
  const [poissonLineError, setPoissonLineError] = useState('');
  const [poissonResult, setPoissonResult] = useState(null);

  // State for Arbitrage Calculator
  const [arbitrageLegs, setArbitrageLegs] = useState(['', '']);
  const [arbitrageLegErrors, setArbitrageLegErrors] = useState(['', '']);
  const [arbitrageTotalStake, setArbitrageTotalStake] = useState('');
  const [arbitrageTotalStakeError, setArbitrageTotalStakeError] = useState('');
  const [arbitrageResult, setArbitrageResult] = useState(null);
  const [arbitrageOddsFormat, setArbitrageOddsFormat] = useState('american');

  // State for Hedge Calculator
  const [hedgeBetOdds, setHedgeBetOdds] = useState('');
  const [hedgeBetAmount, setHedgeBetAmount] = useState('');
  const [hedgeOdds, setHedgeOdds] = useState('');
  const [hedgeBetOddsError, setHedgeBetOddsError] = useState('');
  const [hedgeBetAmountError, setHedgeBetAmountError] = useState('');
  const [hedgeOddsError, setHedgeOddsError] = useState('');
  const [hedgeResult, setHedgeResult] = useState(null);
  const [hedgeOddsFormat, setHedgeOddsFormat] = useState('american');

  // Odds Converter Functions
  const fractionToDecimal = (frac) => {
    const [num, den] = frac.split('/').map(Number);
    if (den === 0) throw new Error('Denominator cannot be zero');
    return (num / den) + 1;
  };

  const decimalToFraction = (dec) => {
    const num = (dec - 1) * 100;
    const den = 100;
    const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
    const divisor = gcd(Math.round(num), den);
    return `${Math.round(num) / divisor}/${den / divisor}`;
  };

  const decimalToAmerican = (dec) => {
    if (dec >= 2) {
      return Math.round((dec - 1) * 100);
    } else {
      return Math.round(-100 / (dec - 1));
    }
  };

  const decimalToImplied = (dec) => {
    return (1 / dec * 100).toFixed(2);
  };

  const handleFractionChange = (value) => {
    setFraction(value);
    if (!value.match(/^\d+\/\d+$/)) return;
    try {
      const dec = fractionToDecimal(value);
      setDecimal(dec.toFixed(2));
      setAmerican(decimalToAmerican(dec));
      setImplied(decimalToImplied(dec));
    } catch {
      setDecimal('');
      setAmerican('');
      setImplied('');
    }
  };

  const handleDecimalChange = (value) => {
    setDecimal(value);
    const dec = parseFloat(value);
    if (isNaN(dec) || dec <= 1) return;
    try {
      setFraction(decimalToFraction(dec));
      setAmerican(decimalToAmerican(dec));
      setImplied(decimalToImplied(dec));
    } catch {
      setFraction('');
      setAmerican('');
      setImplied('');
    }
  };

  const handleAmericanChange = (value) => {
    setAmerican(value);
    const amer = parseInt(value);
    if (isNaN(amer) || amer === 0) return;
    try {
      const dec = amer > 0 ? (amer / 100) + 1 : (100 / Math.abs(amer)) + 1;
      setDecimal(dec.toFixed(2));
      setFraction(decimalToFraction(dec));
      setImplied(decimalToImplied(dec));
    } catch {
      setDecimal('');
      setFraction('');
      setImplied('');
    }
  };

  const handleImpliedChange = (value) => {
    setImplied(value);
    const imp = parseFloat(value);
    if (isNaN(imp) || imp <= 0 || imp >= 100) return;
    try {
      const dec = 100 / imp;
      setDecimal(dec.toFixed(2));
      setFraction(decimalToFraction(dec));
      setAmerican(decimalToAmerican(dec));
    } catch {
      setDecimal('');
      setFraction('');
      setAmerican('');
    }
  };

  // Parlay Calculator Functions
  const oddsToDecimal = (odds, format) => {
    const parsedOdds = parseFloat(odds);
    if (isNaN(parsedOdds)) return null;

    if (format === 'decimal') {
      if (parsedOdds > 1) return parsedOdds;
    } else if (format === 'american') {
      if (parsedOdds > 0) {
        return (parsedOdds / 100) + 1;
      } else if (parsedOdds < 0) {
        return (100 / Math.abs(parsedOdds)) + 1;
      }
    } else if (format === 'fraction') {
      if (odds.match(/^\d+\/\d+$/)) {
        return fractionToDecimal(odds);
      }
    }
    return null;
  };

  const decimalToFormat = (dec, format) => {
    if (format === 'decimal') {
      return dec.toFixed(2);
    } else if (format === 'american') {
      const amer = decimalToAmerican(dec);
      return amer > 0 ? `+${amer}` : amer;
    } else if (format === 'fraction') {
      return decimalToFraction(dec);
    }
    return dec.toFixed(2);
  };

  const calculateParlayOdds = (legs) => {
    const errors = legs.map((odds) => {
      if (!odds) return 'Odds are required';
      const dec = oddsToDecimal(odds, oddsFormat);
      if (dec === null || dec <= 1) return `Invalid ${oddsFormat} odds`;
      return '';
    });
    setParlayLegErrors(errors);

    const probabilities = legs
      .map((odds) => {
        const dec = oddsToDecimal(odds, oddsFormat);
        return dec ? 1 / dec : null;
      })
      .filter((prob) => prob !== null && !isNaN(prob) && prob > 0 && prob < 1);

    if (probabilities.length < 2) {
      setParlayOdds(null);
      return;
    }

    const combinedProbability = probabilities.reduce((acc, prob) => acc * prob, 1);
    const fairParlayDecimal = 1 / combinedProbability;
    setParlayOdds({
      selectedFormat: decimalToFormat(fairParlayDecimal, oddsFormat),
    });
  };

  const handleParlayLegChange = (index, value) => {
    const newLegs = [...parlayLegs];
    newLegs[index] = value;
    setParlayLegs(newLegs);
    calculateParlayOdds(newLegs);
  };

  const addParlayLeg = () => {
    setParlayLegs([...parlayLegs, '']);
    setParlayLegErrors([...parlayLegErrors, '']);
  };

  const removeParlayLeg = (index) => {
    if (parlayLegs.length <= 2) return;
    const newLegs = parlayLegs.filter((_, i) => i !== index);
    const newErrors = parlayLegErrors.filter((_, i) => i !== index);
    setParlayLegs(newLegs);
    setParlayLegErrors(newErrors);
    calculateParlayOdds(newLegs);
  };

  // Poisson Calculator Functions
  const factorial = (n) => {
    if (n <= 1) return 1;
    return n * factorial(n - 1);
  };

  const poissonProbability = (k, lambda) => {
    return (Math.pow(lambda, k) * Math.exp(-lambda)) / factorial(k);
  };

  const calculatePoisson = (mean, line) => {
    const lambda = parseFloat(mean);
    const lineValue = parseFloat(line);
    const floorLine = Math.floor(lineValue);

    let cumulativeProb = 0;
    for (let k = 0; k <= floorLine; k++) {
      cumulativeProb += poissonProbability(k, lambda);
    }

    const probLower = cumulativeProb;
    const probHigher = 1 - probLower;

    return {
      lower: (probLower * 100).toFixed(2),
      higher: (probHigher * 100).toFixed(2),
    };
  };

  const handlePoissonMeanChange = (value) => {
    setPoissonMean(value);
    const mean = parseFloat(value);
    const isMeanValid = !isNaN(mean) && mean > 0;
    setPoissonMeanError(isMeanValid ? '' : 'Mean must be a positive number');

    const line = parseFloat(poissonLine);
    const isLineValid = !isNaN(line) && line >= 0 && line % 1 === 0.5;

    if (isMeanValid && isLineValid) {
      setPoissonResult(calculatePoisson(mean, line));
    } else {
      setPoissonResult(null);
    }
  };

  const handlePoissonLineChange = (value) => {
    setPoissonLine(value);
    const line = parseFloat(value);
    const isLineValid = !isNaN(line) && line >= 0 && line % 1 === 0.5;
    setPoissonLineError(
      isLineValid ? '' :
      !isNaN(line) && line >= 0 ? 'Line must be in half-point increments (e.g., 1.5)' :
      'Line must be a non-negative number'
    );

    const mean = parseFloat(poissonMean);
    const isMeanValid = !isNaN(mean) && mean > 0;

    if (isMeanValid && isLineValid) {
      setPoissonResult(calculatePoisson(mean, line));
    } else {
      setPoissonResult(null);
    }
  };

  // Arbitrage Calculator Functions
  const calculateArbitrage = (legs, totalStake) => {
    const stakeValue = parseFloat(totalStake);
    if (isNaN(stakeValue) || stakeValue <= 0) {
      setArbitrageTotalStakeError('Total stake must be a positive number');
      setArbitrageResult(null);
      return;
    }
    setArbitrageTotalStakeError('');

    const errors = legs.map((odds) => {
      if (!odds) return 'Odds are required';
      const dec = oddsToDecimal(odds, arbitrageOddsFormat);
      if (dec === null || dec <= 1) return `Invalid ${arbitrageOddsFormat} odds`;
      return '';
    });
    setArbitrageLegErrors(errors);

    const decimalOdds = legs
      .map((odds) => oddsToDecimal(odds, arbitrageOddsFormat))
      .filter((dec) => dec !== null && !isNaN(dec) && dec > 1);

    if (decimalOdds.length < 2) {
      setArbitrageResult(null);
      return;
    }

    const sumInverseOdds = decimalOdds.reduce((sum, odds) => sum + (1 / odds), 0);

    if (sumInverseOdds >= 1) {
      setArbitrageResult({ isArbitrage: false });
      return;
    }

    const stakes = decimalOdds.map((odds) => {
      return (stakeValue * (1 / odds)) / sumInverseOdds;
    });

    const payout = stakes[0] * decimalOdds[0];
    const profit = payout - stakeValue;

    setArbitrageResult({
      isArbitrage: true,
      stakes: stakes.map((stake) => stake.toFixed(2)),
      profit: profit.toFixed(2),
    });
  };

  const handleArbitrageLegChange = (index, value) => {
    const newLegs = [...arbitrageLegs];
    newLegs[index] = value;
    setArbitrageLegs(newLegs);
    calculateArbitrage(newLegs, arbitrageTotalStake);
  };

  const handleArbitrageTotalStakeChange = (value) => {
    setArbitrageTotalStake(value);
    calculateArbitrage(arbitrageLegs, value);
  };

  // Hedge Calculator Functions
  const calculateHedge = (betOdds, betAmount, hedgeOddsValue) => {
    const betDec = oddsToDecimal(betOdds, hedgeOddsFormat);
    const hedgeDec = oddsToDecimal(hedgeOddsValue, hedgeOddsFormat);
    const stake = parseFloat(betAmount);

    // Validate inputs
    let hasError = false;
    
    if (!betOdds || betDec === null || betDec <= 1) {
      setHedgeBetOddsError('Valid odds required');
      hasError = true;
    } else {
      setHedgeBetOddsError('');
    }

    if (isNaN(stake) || stake <= 0) {
      setHedgeBetAmountError('Positive amount required');
      hasError = true;
    } else {
      setHedgeBetAmountError('');
    }

    if (!hedgeOddsValue || hedgeDec === null || hedgeDec <= 1) {
      setHedgeOddsError('Valid odds required');
      hasError = true;
    } else {
      setHedgeOddsError('');
    }

    if (hasError) {
      setHedgeResult(null);
      return;
    }

    // Calculate hedge amount to lock in equal profit on both outcomes
    // If original wins: payout = stake * betDec, profit = stake * betDec - stake - hedgeStake
    // If hedge wins: payout = hedgeStake * hedgeDec, profit = hedgeStake * hedgeDec - stake - hedgeStake
    // Set equal: stake * betDec - stake - hedgeStake = hedgeStake * hedgeDec - stake - hedgeStake
    // Simplifies to: hedgeStake = (stake * betDec) / hedgeDec
    const hedgeStake = (stake * betDec) / hedgeDec;
    
    // Guaranteed profit = payout from original - total cost
    const guaranteedProfit = (stake * betDec) - stake - hedgeStake;

    setHedgeResult({
      hedgeAmount: hedgeStake.toFixed(2),
      profit: guaranteedProfit.toFixed(2),
    });
  };

  const handleHedgeBetOddsChange = (value) => {
    setHedgeBetOdds(value);
    calculateHedge(value, hedgeBetAmount, hedgeOdds);
  };

  const handleHedgeBetAmountChange = (value) => {
    setHedgeBetAmount(value);
    calculateHedge(hedgeBetOdds, value, hedgeOdds);
  };

  const handleHedgeOddsChange = (value) => {
    setHedgeOdds(value);
    calculateHedge(hedgeBetOdds, hedgeBetAmount, value);
  };

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', p: 2, bgcolor: 'background.default', minHeight: '100vh', ml: 0 }}>
          <Grid container spacing={2}>
            {/* Left Column: Odds Converter + Parlay */}
            <Grid item xs={12} md={6}>
              {/* Odds Converter Calculator */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: 'primary.main', mb: 2, fontWeight: 600, fontSize: '1.25rem' }}>
                    Odds Converter
                  </Typography>
                  <Stack direction="row" spacing={2} useFlexGap sx={{ maxWidth: '600px', flexWrap: 'wrap', '& > *': { flex: '1 1 120px', minWidth: 0 } }}>
                    <TextField
                      label="Fraction"
                      value={fraction}
                      onChange={(e) => handleFractionChange(e.target.value)}
                      placeholder="e.g., 5/1"
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ 
                        shrink: true
                      }}
                      sx={{ 
                        minWidth: '100px',
                        '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      }}
                    />
                    <TextField
                      label="Decimal"
                      value={decimal}
                      onChange={(e) => handleDecimalChange(e.target.value)}
                      placeholder="e.g., 6.00"
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ 
                        shrink: true
                      }}
                      sx={{ 
                        minWidth: '100px',
                        '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      }}
                    />
                    <TextField
                      label="American"
                      value={american}
                      onChange={(e) => handleAmericanChange(e.target.value)}
                      placeholder="e.g., +500"
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ 
                        shrink: true
                      }}
                      sx={{ 
                        minWidth: '100px',
                        '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      }}
                    />
                    <TextField
                      label="Implied (%)"
                      value={implied}
                      onChange={(e) => handleImpliedChange(e.target.value)}
                      placeholder="e.g., 16.67"
                      size="small"
                      InputLabelProps={{ 
                        shrink: true
                      }}
                      sx={{ 
                        minWidth: '100px',
                        '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>

              {/* Parlay Calculator */}
              <Card sx={{ mb: 2, mt: 2 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: 'primary.main', mb: 2, fontWeight: 600, fontSize: '1.25rem' }}>
                    Parlay Calculator
                  </Typography>
                  <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mb: 2 }}>
                    <InputLabel id="parlay-odds-format-label" shrink>Odds Format</InputLabel>
                    <Select
                      labelId="parlay-odds-format-label"
                      label="Odds Format"
                      notched
                      value={oddsFormat}
                      onChange={(e) => {
                        setOddsFormat(e.target.value);
                        calculateParlayOdds(parlayLegs);
                      }}
                    >
                      <MenuItem value="decimal">Decimal</MenuItem>
                      <MenuItem value="american">American</MenuItem>
                      <MenuItem value="fraction">Fraction</MenuItem>
                    </Select>
                  </FormControl>
                  {parlayLegs.map((leg, index) => (
                    <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <TextField
                        label={`Leg ${index + 1} Odds`}
                        value={leg}
                        onChange={(e) => handleParlayLegChange(index, e.target.value)}
                        placeholder={
                          oddsFormat === 'decimal' ? 'e.g., 2.50' :
                          oddsFormat === 'american' ? 'e.g., +200' :
                          'e.g., 2/1'
                        }
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ 
                          shrink: true,
                          sx: { paddingRight: '8px' }
                        }}
                        error={!!parlayLegErrors[index]}
                        helperText={parlayLegErrors[index]}
                        sx={{ 
                          minWidth: 120,
                          '& .MuiInputBase-input': { color: '#ffffff' },
                          '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                        }}
                      />
                      {parlayLegs.length > 2 && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => removeParlayLeg(index)}
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                  ))}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={addParlayLeg}
                    sx={{ mb: 2 }}
                  >
                    Add Another Leg
                  </Button>
                </CardContent>
              </Card>
              <Box sx={{ p: 2, bgcolor: '#2a2a2a', borderRadius: 0, maxWidth: '448px', mt: 1 }}>
                <Typography>Fair Parlay Odds: {parlayOdds ? parlayOdds.selectedFormat : '-'}</Typography>
              </Box>

              {/* Hedge Calculator */}
              <Card sx={{ mb: 2, mt: 2 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: 'primary.main', mb: 2, fontWeight: 600, fontSize: '1.25rem' }}>
                    Hedge Calculator
                  </Typography>
                  <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mb: 2 }}>
                    <InputLabel id="hedge-odds-format-label" shrink>Odds Format</InputLabel>
                    <Select
                      labelId="hedge-odds-format-label"
                      label="Odds Format"
                      notched
                      value={hedgeOddsFormat}
                      onChange={(e) => {
                        setHedgeOddsFormat(e.target.value);
                        calculateHedge(hedgeBetOdds, hedgeBetAmount, hedgeOdds);
                      }}
                    >
                      <MenuItem value="decimal">Decimal</MenuItem>
                      <MenuItem value="american">American</MenuItem>
                      <MenuItem value="fraction">Fraction</MenuItem>
                    </Select>
                  </FormControl>
                  <Stack spacing={2}>
                    <TextField
                      label="Original Bet Odds"
                      value={hedgeBetOdds}
                      onChange={(e) => handleHedgeBetOddsChange(e.target.value)}
                      placeholder={
                        hedgeOddsFormat === 'decimal' ? 'e.g., 3.00' :
                        hedgeOddsFormat === 'american' ? 'e.g., +200' :
                        'e.g., 2/1'
                      }
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ 
                        shrink: true,
                        sx: { paddingRight: '8px' }
                      }}
                      error={!!hedgeBetOddsError}
                      helperText={hedgeBetOddsError}
                      sx={{ 
                        minWidth: 150,
                        '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      }}
                    />
                    <TextField
                      label="Original Bet Amount"
                      value={hedgeBetAmount}
                      onChange={(e) => handleHedgeBetAmountChange(e.target.value)}
                      placeholder="e.g., 100.00"
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ 
                        shrink: true,
                        sx: { paddingRight: '8px' }
                      }}
                      error={!!hedgeBetAmountError}
                      helperText={hedgeBetAmountError}
                      sx={{ 
                        minWidth: 150,
                        '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      }}
                    />
                    <TextField
                      label="Hedge Odds"
                      value={hedgeOdds}
                      onChange={(e) => handleHedgeOddsChange(e.target.value)}
                      placeholder={
                        hedgeOddsFormat === 'decimal' ? 'e.g., 1.67' :
                        hedgeOddsFormat === 'american' ? 'e.g., -150' :
                        'e.g., 2/3'
                      }
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ 
                        shrink: true,
                        sx: { paddingRight: '8px' }
                      }}
                      error={!!hedgeOddsError}
                      helperText={hedgeOddsError}
                      sx={{ 
                        minWidth: 150,
                        '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
              <Box sx={{ p: 2, bgcolor: '#2a2a2a', borderRadius: 0, maxWidth: '448px', mt: 1 }}>
                <Stack>
                  <Typography>Hedge Amount: {hedgeResult ? `$${hedgeResult.hedgeAmount}` : '-'}</Typography>
                  <Typography>Guaranteed Profit: {hedgeResult ? `$${hedgeResult.profit}` : '-'}</Typography>
                </Stack>
              </Box>
            </Grid>

            {/* Right Column: Poisson + Arbitrage */}
            <Grid item xs={12} md={6}>
              {/* Poisson Calculator */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: 'primary.main', mb: 2, fontWeight: 600, fontSize: '1.25rem' }}>
                    Poisson Calculator
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Mean"
                      value={poissonMean}
                      onChange={(e) => handlePoissonMeanChange(e.target.value)}
                      placeholder="e.g., 1.23"
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ 
                        shrink: true,
                        sx: { paddingRight: '8px' }
                      }}
                      error={!!poissonMeanError}
                      helperText={poissonMeanError}
                      sx={{ 
                        minWidth: 100,
                        '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      }}
                    />
                    <TextField
                      label="Line"
                      value={poissonLine}
                      onChange={(e) => handlePoissonLineChange(e.target.value)}
                      placeholder="e.g., 1.5"
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ 
                        shrink: true,
                        sx: { paddingRight: '8px' }
                      }}
                      error={!!poissonLineError}
                      helperText={poissonLineError}
                      sx={{ 
                        minWidth: 100,
                        '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
              <Box sx={{ p: 2, bgcolor: '#2a2a2a', borderRadius: 0, maxWidth: '448px', mt: 1 }}>
                <Stack>
                  <Typography>Probability Lower than {poissonLine || 'Line'}: {poissonResult ? `${poissonResult.lower}%` : '-'}</Typography>
                  <Typography>Probability Higher than {poissonLine || 'Line'}: {poissonResult ? `${poissonResult.higher}%` : '-'}</Typography>
                </Stack>
              </Box>

              {/* Arbitrage Calculator */}
              <Card sx={{ mb: 2, mt: 2 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: 'primary.main', mb: 2, fontWeight: 600, fontSize: '1.25rem' }}>
                    Arbitrage Calculator
                  </Typography>
                  <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mb: 2 }}>
                    <InputLabel id="arbitrage-odds-format-label" shrink>Odds Format</InputLabel>
                    <Select
                      labelId="arbitrage-odds-format-label"
                      label="Odds Format"
                      notched
                      value={arbitrageOddsFormat}
                      onChange={(e) => {
                        setArbitrageOddsFormat(e.target.value);
                        calculateArbitrage(arbitrageLegs, arbitrageTotalStake);
                      }}
                    >
                      <MenuItem value="decimal">Decimal</MenuItem>
                      <MenuItem value="american">American</MenuItem>
                      <MenuItem value="fraction">Fraction</MenuItem>
                    </Select>
                  </FormControl>
                  {arbitrageLegs.map((leg, index) => (
                    <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <TextField
                        label={`Outcome ${index + 1} Odds`}
                        value={leg}
                        onChange={(e) => handleArbitrageLegChange(index, e.target.value)}
                        placeholder={
                          arbitrageOddsFormat === 'decimal' ? 'e.g., 2.50' :
                          arbitrageOddsFormat === 'american' ? 'e.g., +150' :
                          'e.g., 3/2'
                        }
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ 
                          shrink: true,
                          sx: { paddingRight: '12px' } // Larger padding for longer label
                        }}
                        error={!!arbitrageLegErrors[index]}
                        helperText={arbitrageLegErrors[index]}
                        sx={{ 
                          minWidth: 150,
                          '& .MuiInputBase-input': { color: '#ffffff' },
                          '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                        }}
                      />
                    </Stack>
                  ))}
                  <TextField
                    label="Total Stake"
                    value={arbitrageTotalStake}
                    onChange={(e) => handleArbitrageTotalStakeChange(e.target.value)}
                    placeholder="e.g., 100.00"
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ 
                      shrink: true,
                      sx: { paddingRight: '8px' }
                    }}
                    error={!!arbitrageTotalStakeError}
                    helperText={arbitrageTotalStakeError}
                    sx={{ 
                      minWidth: 120,
                      '& .MuiInputBase-input': { color: '#ffffff' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    }}
                  />
                </CardContent>
              </Card>
              <Box sx={{ p: 2, bgcolor: '#2a2a2a', borderRadius: 0, maxWidth: '448px', mt: 1 }}>
                <Stack>
                  <Typography>Arbitrage Opportunity: {arbitrageResult && arbitrageResult.isArbitrage ? 'Exists!' : '-'}</Typography>
                  {arbitrageLegs.map((_, index) => (
                    <Typography key={index}>Stake on Outcome {index + 1}: {arbitrageResult && arbitrageResult.isArbitrage ? `$${arbitrageResult.stakes[index]}` : '-'}</Typography>
                  ))}
                  <Typography>Guaranteed Profit: {arbitrageResult && arbitrageResult.isArbitrage ? `$${arbitrageResult.profit}` : '-'}</Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Calculators;